import React from 'react';
import type { PortableTextBlock } from '@sanity/types';
import { bgColorClassname } from '@/utils';
import { LinkInterface } from '@/types';
import { RichText, Heading } from '@/components';
import {
  Accordion as BaseAccordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// Define the structure for an individual FAQ
interface FAQ {
  question: string;
  answer: PortableTextBlock;
}

// Define the structure for a FAQ topic, which includes multiple FAQs
interface FAQTopic {
  name: string;
  description?: string;
  faqs: FAQ[];
  includeCta: boolean;
  cta?: LinkInterface;
}

// Define the props for the FAQContentLayer component
interface FAQProps {
  backgroundColor?: {
    label: string;
    value: string;
  };
  faqTopic: FAQTopic;
}

// Component to display FAQ content with optional background color and CTA
const FAQContentLayer: React.FC<FAQProps> = ({ backgroundColor, faqTopic }) => {
  return (
    <div className={backgroundColor ? bgColorClassname(backgroundColor) : ''}>
      <div className='mx-auto flex max-w-screen-xl flex-col px-5 py-8 md:p-[50px]'>
        {faqTopic && (
          <>
            {/* Display the FAQ topic name and optional CTA */}
            <Heading
              title={faqTopic.name}
              isShowButton={faqTopic.includeCta}
              cta={faqTopic.cta}
              className='mb-5 md:mb-8'
            />
            {/* Display the FAQ topic description if it exists */}
            {faqTopic.description && (
              <p className='mb-5 text-xl font-normal md:mb-8'>{faqTopic.description}</p>
            )}
            {/* Accordion component to display the list of FAQs */}
            <BaseAccordion
              type='single'
              collapsible
              className='w-full space-y-3.5'
              defaultValue='item-0'
            >
              {faqTopic.faqs &&
                faqTopic.faqs.map((faq, index) => (
                  <AccordionItem
                    value={`item-${index}`}
                    key={`accordion-item-${index}`}
                    className='rounded-lg border-b-0 bg-white px-5 py-[18px] md:px-6'
                  >
                    <AccordionTrigger className='gap-2.5 p-0 hover:no-underline [&[data-state]>svg]:h-6 [&[data-state]>svg]:w-6 [&[data-state]>svg]:rounded-full [&[data-state]>svg]:bg-yellow-500 [&[data-state]>svg]:p-1'>
                      <span className='text-left text-xl font-semibold leading-6 md:text-2xl md:font-medium md:leading-7'>
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className='pb-0 pt-3.5 md:pt-[18px]'>
                      <RichText
                        className='accordion-content-wrapper text-lg font-light'
                        value={faq.answer}
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}
            </BaseAccordion>
          </>
        )}
      </div>
    </div>
  );
};

export default FAQContentLayer;
